import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { projectsInstance } from "@/services/ProjectsService";
import { IProgressReport } from "@/types/report";
import { useReportsStore } from "@/store/store";
import { useWorkspace } from "@/context/WorkspaceContext";

export const useReports = (token: string | null) => {
  const { activeWorkspace, loading: workspaceLoading, refreshWorkspaces } = useWorkspace();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const refetchRef = useRef<() => void>();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  /** Status each tracked report had on the previous tick, to spot transitions. */
  const previousStatusesRef = useRef<Record<number, string>>({});
  // Kept out of the effect's deps below: calling it updates the workspace
  // context, which re-creates the callback — so depending on it would re-run
  // that effect (re-arming the poll interval) as a result of its own work.
  const refreshWorkspacesRef = useRef(refreshWorkspaces);
  refreshWorkspacesRef.current = refreshWorkspaces;
  // Selector, not a bare subscription: this hook runs in the app shell now
  // (AITS-540), and a bare `useReportsStore()` re-renders its host on every
  // one of its own 5s ticks — which would re-render the whole routed page.
  const setGeneratingReports = useReportsStore((s) => s.setGeneratingReports);

  const query = useQuery({
    queryKey: ["reports", activeWorkspace?.id],
    queryFn: async () => {
      const { results } = await projectsInstance.getReports(tokenRef.current);

      return results.slice(0, 5);
    },
    enabled: !!token && !workspaceLoading && !!activeWorkspace?.id,
    refetchInterval: false,
    // Only re-render for payload changes. Spreading the result read every key of
    // React Query's tracked-props proxy, which subscribed this hook to
    // `isFetching`/`fetchStatus` too — harmless on the old Home screen, but it
    // now runs in the shell, so each poll re-rendered every page twice.
    notifyOnChangeProps: ["data"],
  });
  const data = query.data;

  useEffect(() => {
    refetchRef.current = query.refetch;
  }, [query.refetch]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (data) {
      // Track every recent report by id — including completed ones — so an open
      // GenerateDialog can still observe the moment its report flips to
      // "completed" and redirect. (Previously completed reports were evicted
      // here, so a report finishing between polls vanished from the store and
      // the dialog got stranded at "Preparing report status..." / 0%.)
      const trackedReports: Record<number, IProgressReport> = {};
      let hasInProgressLocal = false;

      data.forEach((report) => {
        trackedReports[report.id] = report as IProgressReport;
        // Only genuinely in-flight reports keep the poll alive. `!== "completed"`
        // also matched "error", so one failed report made the shell poll every
        // 5s forever, on every page, for every member of the workspace.
        if (report.status === "in_progress" || report.status === "finalizing") {
          hasInProgressLocal = true;
        }
      });

      setGeneratingReports(trackedReports);

      // A report that just stopped building changes what the *workspace* payload
      // says about its company, and that payload is what the sidebar reads for
      // its per-company "in progress" label. Nothing else refetches it on
      // completion — `refreshWorkspaces` is wired to explicit user actions only
      // (create a report, re-run, edit settings) — so the sidebar sat on
      // "in progress" for a finished report until the user reloaded the app.
      const previous = previousStatusesRef.current;
      const wasBuilding = (id: number) =>
        previous[id] === "in_progress" || previous[id] === "finalizing";
      const settled = data.some(
        (report) =>
          (report.status === "completed" || report.status === "error") &&
          wasBuilding(report.id),
      );
      previousStatusesRef.current = Object.fromEntries(
        data.map((report) => [report.id, report.status]),
      );
      if (settled) {
        void refreshWorkspacesRef.current();
      }

      // Announce every completion to everyone EXCEPT the tab that started the
      // run — that tab is handled by `useAwaitedReport` (navigation, or its
      // own toast when the user is mid-work). This is what a workspace member
      // watching or ignoring a colleague's build gets instead of being routed.
      // Persistent until closed: a completion shouldn't evaporate while the
      // user is away — a background tab observes it on a throttled tick and
      // the toast is still there when they come back.
      const awaitedId = useReportsStore.getState().awaitedReportId;
      data
        .filter(
          (report) =>
            report.status === "completed" &&
            wasBuilding(report.id) &&
            report.id !== awaitedId,
        )
        .forEach((report) => {
          toast.success(
            report.company_name
              ? `The report for ${report.company_name} is ready`
              : "The report is ready",
            {
              id: `report-ready-${report.id}`,
              duration: Infinity,
              closeButton: true,
              action: {
                label: "View",
                onClick: () =>
                  navigateRef.current(`/reports/${report.company_id}/${report.id}`),
              },
            },
          );
        });

      if (hasInProgressLocal) {
        intervalRef.current = setInterval(() => {
          if (refetchRef.current) {
            refetchRef.current();
          }
        }, 5000);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [data, setGeneratingReports]);

  // Catch up the moment the user comes back to the app.
  //
  // React Query's own focus refetch listens to `visibilitychange`, which does
  // not fire when you switch to a *different window* and the tab stays visible
  // behind it — exactly how this was reported (left open on macOS, worked in
  // another window). Timers in an unfocused window are throttled hard, so the
  // 5s poll above can be minutes late. A plain `focus` listener covers the gap.
  useEffect(() => {
    const onFocus = () => refetchRef.current?.();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // Nothing consumes a return value: the shell mounts this purely for its side
  // effect of filling the progress store (AITS-540), and the last reader —
  // ReRunReportModal — now invalidates the query directly instead.
};
